import { FC } from 'react';
import {
  Trigger,
  Portal,
  Content,
  Close,
  Arrow,
  Root,
} from '@radix-ui/react-popover';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button, Checkbox, TooltipInfo } from '@/components';
import { ButtonColor, ButtonSize, Icons } from '@/enums';
import strings from '@/language/Swedish';
import { RegistryColumnType } from './registryConfig';

const StyledContent = styled(Content)`
  ${tw`rounded-xl z-50 mr-4 bg-white border shadow-xl backdrop-blur border-white`};
`;

const TopContainer = styled.div`
  ${tw`flex items-center justify-center gap-1 rounded-t-xl p-1 bg-gradient-to-b from-white to-white/0 border-b border-slate-200`};
`;

const Header = styled.p`
  ${tw`flex-1 text-sm mx-2 my-0 text-slate-700`};
`;

const CheckboxContainer = styled.div`
  ${tw`py-2 px-4 gap-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3`};
`;

const StyledCheckbox = styled(Checkbox)`
  ${tw`py-1`};
`;

const StyledArrow = styled(Arrow)`
  transform: translateX(-14px);
`;

interface Props {
  columns: RegistryColumnType[];
  visibleColumns: Record<string, boolean>;
  toggleColumn: (name: string) => void;
}

const RegistryColumnDialog: FC<Props> = ({
  columns,
  visibleColumns,
  toggleColumn,
}) => (
  <Root modal={false}>
    <Trigger asChild>
      <div>
        <TooltipInfo content={strings.registry.chooseColumnsInfo}>
          <div>
            <Button
              icon={Icons.columns}
              color={ButtonColor.border}
              size={ButtonSize.medium}
              name="select columns"
            />
          </div>
        </TooltipInfo>
      </div>
    </Trigger>
    <Portal>
      <StyledContent
        sideOffset={5}
        collisionPadding={10}
        avoidCollisions={true}
        align="end"
      >
        <TopContainer>
          <Header>{strings.registry.chooseColumns}</Header>
          <Close asChild aria-label="Close">
            <div>
              <Button
                color={ButtonColor.transparent}
                size={ButtonSize.small}
                icon={Icons.cross}
                aria-label="Close"
              />
            </div>
          </Close>
        </TopContainer>
        <CheckboxContainer>
          {columns.map((column) => (
            <StyledCheckbox
              id={`registry-column-${column.name}`}
              key={column.name}
              label={column.label}
              isChecked={visibleColumns[column.name] ?? false}
              onToggle={() => toggleColumn(column.name)}
            />
          ))}
        </CheckboxContainer>
        <StyledArrow className="fill-white" />
      </StyledContent>
    </Portal>
  </Root>
);

export default RegistryColumnDialog;
